import * as React from 'react';
import { IconButton, Tooltip, Typography } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { SkeletonLoader } from '../components/loading/SkeletonLoader';
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            height: '100%',
            borderRadius: 10,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            cursor: 'pointer',
            transition: 'box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease',
            display: 'flex',
            flexDirection: 'column',
            padding: theme.spacing(2, 2, 1.5, 2),
            '&:hover': {
                boxShadow: '0 4px 18px rgba(0,0,0,0.10)',
                borderColor: theme.palette.primary.light,
                transform: 'translateY(-1px)',
            },
            '&:hover $chevron': {
                opacity: 1,
                transform: 'translateX(2px)',
            },
        },
        skeletonRoot: {
            position: 'relative',
            height: '100%',
            borderRadius: 10,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            padding: theme.spacing(2, 2, 1.5, 2),
        },
        iconWrap: {
            width: 40,
            height: 40,
            borderRadius: 10,
            backgroundColor: '#e3f2fd',
            color: theme.palette.primary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: theme.spacing(1.5),
            flexShrink: 0,
            '& svg': {
                fontSize: 22,
            },
        },
        title: {
            fontSize: 13,
            fontWeight: 600,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(0.5),
            lineHeight: 1.3,
            paddingRight: theme.spacing(3.5),
        },
        description: {
            fontSize: 12,
            color: theme.palette.text.secondary,
            lineHeight: 1.5,
            flex: 1,
        },
        actions: {
            position: 'absolute',
            top: 8,
            right: 8,
            display: 'flex',
            gap: 2,
        },
        actionBtn: {
            padding: 4,
            color: theme.palette.text.disabled,
            '&:hover': {
                color: theme.palette.primary.main,
                backgroundColor: '#e3f2fd',
            },
        },
        chevron: {
            position: 'absolute',
            bottom: 10,
            right: 10,
            color: theme.palette.primary.light,
            opacity: 0,
            transition: 'opacity 0.15s ease, transform 0.15s ease',
            fontSize: 18,
        },
        badgeRow: {
            display: 'flex',
            flexWrap: 'wrap' as const,
            gap: theme.spacing(0.75),
            marginTop: theme.spacing(1.5),
            paddingTop: theme.spacing(1.25),
            borderTop: `1px solid ${theme.palette.divider}`,
        },
        badgeChip: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '3px 10px',
            borderRadius: 20,
            backgroundColor: theme.palette.grey[100],
            fontSize: 11,
            color: theme.palette.text.secondary,
            cursor: 'pointer',
            transition: 'background-color 0.12s ease',
            '&:hover': {
                backgroundColor: '#e3f2fd',
                color: theme.palette.primary.main,
            },
        },
        badgeCount: {
            fontWeight: 700,
            color: theme.palette.primary.main,
            fontSize: 12,
        },
        meta: {
            fontSize: 11,
            fontStyle: 'italic',
            fontWeight: 600,
            color: theme.palette.text.secondary,
            marginTop: theme.spacing(0.5),
        },
        linksWrapper: {
            display: 'flex',
            flexDirection: 'column' as const,
            gap: theme.spacing(0.5),
            marginTop: theme.spacing(1),
        },
        link: {
            fontSize: 12,
            color: theme.palette.primary.main,
            cursor: 'pointer',
            textAlign: 'left' as const,
            '&:hover': {
                textDecoration: 'underline',
            },
        },
    })
);

interface Item {
    name: string;
    onClick?: () => void;
    count: number;
    maxCount?: number;
}

interface CardLink {
    label: string;
    href?: string;
    download?: boolean;
    target?: string;
    onClick?: (event: React.MouseEvent) => void;
}

interface CommonMaterialCardProps {
    icon?: React.ReactNode;
    title?: string;
    description?: string;
    onClick?: () => void;
    items?: Item[];
    onLearnMore?: () => void;
    onDocument?: () => void;
    loading?: boolean;
    /* Card shell (icon/title/description) is shown immediately; only the chip row is skeletoned */
    itemsLoading?: boolean;
    itemsSkeletonCount?: number;
    /* Short secondary line below the description, e.g. a video duration */
    meta?: string;
    /* Secondary clickable links stacked below the description/meta, e.g. extra document/video links */
    links?: CardLink[];
}

export default function CommonMaterialCard(props: CommonMaterialCardProps) {
    const classes = useStyles();
    const { icon, title, description, onClick, items, onLearnMore, onDocument, loading, itemsLoading, itemsSkeletonCount, meta, links } = props;

    if (loading) {
        return (
            <div className={classes.skeletonRoot}>
                <SkeletonLoader variant="card" rows={2} />
            </div>
        );
    }

    return (
        <div className={classes.root} onClick={onClick}>

            <div className={classes.iconWrap}>
                {icon}
            </div>

            <Typography className={classes.title}>{title}</Typography>
            <Typography className={classes.description}>{description}</Typography>
            {meta && <Typography className={classes.meta}>{meta}</Typography>}

            {links && links.length > 0 && (
                <div className={classes.linksWrapper}>
                    {links.map((link, index) => {
                        const handleLinkClick = (event: React.MouseEvent) => {
                            event.stopPropagation();
                            link.onClick?.(event);
                        };
                        return link.href ? (
                            <a
                                key={index}
                                className={classes.link}
                                href={link.href}
                                download={link.download}
                                target={link.target}
                                rel={link.target === '_blank' ? 'noopener' : undefined}
                                onClick={handleLinkClick}
                            >
                                {link.label}
                            </a>
                        ) : (
                            <span key={index} className={classes.link} onClick={handleLinkClick}>
                                {link.label}
                            </span>
                        );
                    })}
                </div>
            )}

            {itemsLoading ? (
                <div className={classes.badgeRow}>
                    {Array.from({ length: itemsSkeletonCount ?? 3 }).map((_, index) => (
                        <SkeletonLoader key={index} variant="shape" shape="rect" width={70} height={24} style={{ borderRadius: 20 }} />
                    ))}
                </div>
            ) : items && items.length > 0 && (
                <div className={classes.badgeRow}>
                    {items.map((item, index) => (
                        <span
                            key={index}
                            className={classes.badgeChip}
                            onClick={e => { e.stopPropagation(); item.onClick?.(); }}
                        >
                            <span className={classes.badgeCount}>{item.count}</span>
                            {item.name}
                        </span>
                    ))}
                </div>
            )}

            {(onLearnMore || onDocument) && (
                <div className={classes.actions} onClick={e => e.stopPropagation()}>
                    {onLearnMore && (
                        <Tooltip title="Learn More">
                            <IconButton size="small" className={classes.actionBtn} onClick={onLearnMore}>
                                <VideoLibraryOutlinedIcon style={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {onDocument && (
                        <Tooltip title="More Information">
                            <IconButton size="small" className={classes.actionBtn} onClick={onDocument}>
                                <ErrorOutlineIcon style={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            )}

            <ChevronRightIcon className={classes.chevron} />

        </div>
    );
}
